import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  clearCategoryMessages,
} from "../redux/slices/categorySlice";
import Swal from "sweetalert2";

const ManageCategory = () => {
  const dispatch = useDispatch();
  const { categories, pagination, loading, error, successMessage } = useSelector(
    (state) => state.categories
  );

  const [categoryName, setCategoryName] = useState("");
  const [categoryImage, setCategoryImage] = useState(null);
  const [editId, setEditId] = useState(null);

  const [page, setPage] = useState(1);

  // Fetch categories with pagination
  useEffect(() => {
    dispatch(fetchCategories({ page, limit: 10 }));
  }, [dispatch, page]);

  // Handle success/error messages with sweetalert
  useEffect(() => {
    if (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error,
      });
      dispatch(clearCategoryMessages());
    }

    if (successMessage) {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: successMessage,
      });
      dispatch(clearCategoryMessages());

      // Refresh after create/edit/delete
      dispatch(fetchCategories({ page }));
    }
  }, [error, successMessage, dispatch, page]);

  // Add or Update Category
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!categoryName) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Category name is required",
      });
      return;
    }

    const formData = new FormData();
    formData.append("name", categoryName);
    if (categoryImage) formData.append("category_image", categoryImage);

    if (editId) {
      dispatch(updateCategory({ id: editId, formData }));
    } else {
      dispatch(createCategory(formData));
    }

    setCategoryName("");
    setCategoryImage(null);
    setEditId(null);
    document.getElementById("closeModalBtn").click();
  };

  // Edit modal data fill
  const handleEdit = (category) => {
    setCategoryName(category.name);
    setCategoryImage(null);
    setEditId(category._id);
  };

  // Delete category
  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Category will be deleted permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteCategory(id));
      }
    });
  };

  return (
    <div className="container my-4">
      {/* HEADER */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Manage Categories</h5>
          <button
            className="btn btn-primary btn-sm"
            data-bs-toggle="modal"
            data-bs-target="#categoryModal"
            onClick={() => {
              setCategoryName("");
              setCategoryImage(null);
              setEditId(null);
            }}
          >
            Add Category
          </button>
        </div>

        {/* TABLE */}
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.length > 0 ? (
                    categories.map((cat, index) => (
                      <tr key={cat._id}>
                        <td>{(page - 1) * pagination.limit + index + 1}</td>

                        <td>
                          {cat.category_image ? (
                            <img
                              src={cat.category_image}
                              alt={cat.name}
                              width="50"
                              height="50"
                              style={{ objectFit: "cover" }}
                            />
                          ) : (
                            "No Image"
                          )}
                        </td>

                        <td>{cat.name}</td>

                        <td>
                          <button
                            className="btn btn-warning btn-sm me-2"
                            data-bs-toggle="modal"
                            data-bs-target="#categoryModal"
                            onClick={() => handleEdit(cat)}
                          >
                            Edit
                          </button>

                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(cat._id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        No categories found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* PAGINATION */}
          <div className="d-flex justify-content-between mt-3">
            <button
              className="btn btn-secondary btn-sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>

            <span className="fw-bold">
              Page {pagination.page} / {pagination.totalPages}
            </span>

            <button
              className="btn btn-secondary btn-sm"
              disabled={page >= pagination.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* MODAL */}
      <div className="modal fade" id="categoryModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog">
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editId ? "Edit Category" : "Add Category"}</h5>
                <button
                  type="button"
                  id="closeModalBtn"
                  className="btn-close"
                  data-bs-dismiss="modal"
                ></button>
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Category Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Category Image</label>
                  <input
                    type="file"
                    className="form-control"
                    onChange={(e) => setCategoryImage(e.target.files[0])}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>

                <button type="submit" className="btn btn-primary">
                  {editId ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ManageCategory;
